// src/stores/lib/TrackManager.ts
import { findLayoutRelatedElements } from '@src/utility/stackedLayout';

import { deepClone } from '../../utility/deepClone';
import { ensureElementZPositions } from '../../utility/general';
// TODO: This should be... somewhere else also.
import {
  findElementStateById,
  getElementPositionProperties,
  getOriginalVideoTrackSourceDiff,
} from '../../utility/timeline';
import { KARAOKE_TRACK_NUMBER } from '../VideoCreatorStore';

import type { ElementState } from '../../renderer/ElementState';
import type { RendererState } from '../../renderer/RendererState';
import type { SourceDiffEntry } from '../../utility/timeline';
import type VideoCreatorStore from '../VideoCreatorStore';

export class TrackManager {
  videoCreator: VideoCreatorStore;

  constructor(videoCreator: VideoCreatorStore) {
    this.videoCreator = videoCreator;
  }

  get videoSourceStore() {
    return this.videoCreator.rootStore.videoSourceStore;
  }

  updateFrameLockedTracksAfterRearrange(
    // Support source or renderer state
    oldStateElements: ElementState[] | Record<string, any>[],
    newStateElements: ElementState[] | Record<string, any>[],
  ) {
    this.videoCreator.frameLockedTracks = this.videoCreator.frameLockedTracks
      .map(track => {
        const elementsInOldSource = oldStateElements.filter(
          el => el.track === track,
        );
        if (elementsInOldSource.length > 0) {
          const elementInNewSource = newStateElements.find(el =>
            elementsInOldSource.find(
              oldEl =>
                // Handle element.id or element.source.id
                (oldEl.source?.id && oldEl.source.id === el.source.id) ||
                oldEl.id === el.id,
            ),
          );
          return elementInNewSource?.track;
        }
      })
      .filter(track => track !== undefined) as number[];
  }

  async rearrangeTracks(
    track: number,
    direction: 'up' | 'down',
  ): Promise<void> {
    if (track === KARAOKE_TRACK_NUMBER) return;

    const renderer = this.videoCreator.renderer;
    if (!renderer || !renderer.state) {
      return;
    }

    // The track number to swap with
    const targetTrack = direction === 'up' ? track + 1 : track - 1;
    if (targetTrack < 1) {
      return;
    }

    // Elements at provided track
    const elementsCurrentTrack = renderer.state.elements.filter(
      element => element.track === track,
    );
    if (elementsCurrentTrack.length === 0) {
      return;
    }

    this.videoCreator.saveVideoStateSnapshot(undefined, `rearranging tracks`);

    await this.moveTrackPastAnother(track, targetTrack);
  }

  async lockVideoTrackOnStateChange(elements: ElementState[]) {
    if (elements.length === 0) return;
    if (this.isVideoTrackLocked(elements)) return;
    if (this.videoCreator.transitionInProgress) return;
    const activeElement = this.videoCreator.getActiveElement();

    if (
      !activeElement ||
      (activeElement &&
        !this.videoCreator.isOriginalVideoElement(activeElement.source))
    ) {
      await this.lockVideoTrack(elements);
    }
  }

  async lockVideoTrack(elements?: ElementState[]) {
    const videoElements = elements?.filter(e =>
      this.videoCreator.isOriginalVideoElement(e.source),
    );
    const modifications = {} as Record<string, any>;

    for (let e of videoElements || []) {
      modifications[`${e.source.id}.locked`] = true;
    }
    if (Object.keys(modifications).length > 0) {
      this.videoCreator.rootStore.videoSourceStore.applyModifications(
        modifications,
      );
    }
  }

  async unLockVideoTrack() {
    const elements = this.videoSourceStore.source.elements;

    const videoElements = elements?.filter((e: Record<string, any>) =>
      this.videoCreator.isOriginalVideoElement(e),
    );
    if (videoElements?.every((e: Record<string, any>) => !e.locked)) return;

    const modifications = {} as Record<string, any>;

    for (let e of videoElements || []) {
      if (e.locked) {
        modifications[`${e.id}.locked`] = false;
      }
    }

    if (Object.keys(modifications).length > 0) {
      this.videoCreator.rootStore.videoSourceStore.applyModifications(
        modifications,
      );
    }
  }

  isVideoTrackLocked(elements?: ElementState[]) {
    const videoElements = elements?.filter(e =>
      this.videoCreator.isOriginalVideoElement(e.source),
    );
    return videoElements?.every(e => e.source.locked);
  }

  toggleTrackFrameLock(track: number) {
    this.videoCreator.frameLockedTracks =
      this.videoCreator.frameLockedTracks.includes(track)
        ? this.videoCreator.frameLockedTracks.filter(t => t !== track)
        : [...this.videoCreator.frameLockedTracks, track];
  }

  async applyChangesToFrameLockedTracks(
    prevState: RendererState,
    newState: RendererState,
  ) {
    if (prevState.width !== newState.width) {
      console.log(
        'Aspect ratio changed, not applying frame locked track changes',
      );
      return false;
    }
    const frameLockedTrackChanges = getOriginalVideoTrackSourceDiff(
      this.videoCreator,
      prevState.elements,
      newState.elements,
    );
    if (Object.keys(frameLockedTrackChanges).length !== 1) {
      console.log(
        'No frame locked tracks changed or more than one, not applying',
      );
      return false;
    }
    const id = Object.keys(frameLockedTrackChanges)[0];
    const trackChanges = getElementPositionProperties(
      this.videoCreator.time,
      findElementStateById(this.videoCreator, newState.elements, id)!,
    );
    // Does frameLockedTrackChanges contain some positional updates?
    // if not, an infinite loop will occur
    if (
      Object.keys(frameLockedTrackChanges[id].changes).every(
        key => !Object.prototype.hasOwnProperty.call(trackChanges, key),
      )
    ) {
      console.log('No positional changes detected, skipping apply');
      return false;
    }
    const frameLockedElements = this.collectFrameLockedElementsToUpdate(
      frameLockedTrackChanges[id],
      newState,
    );
    // no need to apply changes if frame locked track consists of one element
    // and it matches the changed element
    if (
      frameLockedElements.length === 1 &&
      frameLockedElements[0].source.id === id
    ) {
      console.warn('Frame lock track consists of one element, skipping apply');
      return false;
    }
    if (frameLockedElements.length === 0) {
      console.warn('No frame locked elements found, skipping apply');
      return false;
    }

    // If headless editor is enabled, dispatch commands instead of applyModifications
    const { editor, enableHeadlessEditorWave3 } =
      this.videoCreator.rootStore.editorStore;
    if (editor && enableHeadlessEditorWave3) {
      const updatesMap = new Map<string, Record<string, any>>();
      for (const el of frameLockedElements) {
        updatesMap.set(el.source.id, {
          ...trackChanges,
          clip: true,
        });
      }
      const success = editor.commandUpdateMultipleElements(updatesMap);
      return success;
    }

    // Legacy behavior: apply modifications directly
    const modificationObject: any = {};
    for (const element of frameLockedElements) {
      for (const key in trackChanges) {
        modificationObject[`${element.source.id}.${key}`] =
          trackChanges[key] || null;
      }
      modificationObject[`${element.source.id}.clip`] = true;
    }
    if (Object.keys(modificationObject).length > 0) {
      this.videoCreator.rootStore.videoSourceStore.applyModifications(
        modificationObject,
      );
      return true;
    }
    return false;
  }

  private collectFrameLockedElementsToUpdate(
    trackChangeEntry: SourceDiffEntry,
    newState: RendererState,
  ): ElementState[] {
    let result: ElementState[] = [];
    if (trackChangeEntry.extra?.isLayoutChild) {
      result = this.collectFrameLockedStackedChildrenToUpdate(
        trackChangeEntry.extra,
        newState,
      );
    } else {
      result = newState.elements.filter(el =>
        this.videoCreator.frameLockedTracks.includes(el.track),
      );
    }
    return result;
  }

  private collectFrameLockedStackedChildrenToUpdate(
    trackChangeEntryExtra: SourceDiffEntry['extra'],
    newState: RendererState,
  ): ElementState[] {
    const result: ElementState[] = [];
    const { layoutType, layoutPosition, parentTrack, isLayoutChildInner } =
      trackChangeEntryExtra!;
    for (const el of newState.elements) {
      const layoutInfo = findLayoutRelatedElements(
        this.videoCreator,
        el.source.id,
      );
      if (!layoutInfo.isLayoutChild) {
        continue;
      }
      const elementParentTrack = newState.elements.find(
        el => el.source.id === layoutInfo.hiddenParentId,
      )?.track;
      if (
        !(
          layoutInfo.layoutType === layoutType &&
          layoutInfo.layoutPosition === layoutPosition &&
          elementParentTrack === parentTrack
        )
      ) {
        continue;
      }
      // Composition or inner video element?
      if (isLayoutChildInner) {
        const inner = el.elements?.find(e => e.source.type === 'video');
        if (inner) {
          result.push(inner);
        }
      } else {
        result.push(el);
      }
    }
    return result;
  }

  async moveTrackPastAnother(
    track: number,
    anotherTrack: number,
    modifiedSource: Record<string, any> | undefined = undefined,
  ): Promise<void> {
    const trackDiff = track - anotherTrack;
    const renderer = this.videoCreator.renderer;
    if (
      !renderer ||
      !renderer.state ||
      trackDiff === 0 ||
      track === KARAOKE_TRACK_NUMBER
    ) {
      return;
    }

    const oldStateElements = deepClone(renderer.state.elements);
    const source =
      modifiedSource ||
      deepClone(this.videoCreator.rootStore.videoSourceStore.source);

    const direction = trackDiff > 0 ? 'down' : 'up';
    for (let i = 0; i < Math.abs(trackDiff); i++) {
      // The track number to swap with
      const targetTrack = direction === 'up' ? track + 1 : track - 1;
      if (targetTrack < 1) {
        break;
      }
      // Swap track numbers
      for (const element of source.elements) {
        if (element.track === track) {
          element.track = targetTrack;
        } else if (element.track === targetTrack) {
          element.track = track;
        }
      }
      track = direction === 'up' ? track + 1 : track - 1;
    }

    // Set source by the mutated state
    const newSource =
      this.videoCreator.videoTranscriptionProcessor.adjustTrackNumbersToStartFromOne(
        source,
      );

    // Apply ensureElementZPositions to update z-index values based on new track numbers
    const updatedSource = ensureElementZPositions(newSource);

    this.videoCreator.rootStore.videoSourceStore.setSource(updatedSource, true);
    this.updateFrameLockedTracksAfterRearrange(
      oldStateElements,
      renderer.state.elements,
    );
  }

  frameLockOriginalVideoTrack() {
    const elements = this.videoSourceStore.source.elements;
    if (!elements || elements.length === 0) {
      console.warn('No elements found to frame lock original video track');
      return;
    }
    const originalVideoElement = elements.find((el: Record<string, any>) =>
      this.videoCreator.isOriginalVideoElement(el),
    );
    if (!originalVideoElement) {
      console.warn('No original video element found to frame lock');
      return;
    }
    if (
      this.videoCreator.frameLockedTracks.includes(originalVideoElement.track)
    ) {
      console.debug('Original video track is already frame locked');
      return;
    }
    this.toggleTrackFrameLock(originalVideoElement.track);
    console.debug('Original video track frame locked');
  }
}
